import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import ActivityType from 'enums/ActivityType';
import { Chip, Stack, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ActivitiesListItemSentimentOverview from './ActivitiesListItemSentimentOverview';
import PollActivityVotesIndicator from './PollActivityVotesIndicator';
import PollActivityEngagementIndicator from './PollActivityEngagementIndicator';
import TextPromptActivityResponsesIndicator from './TextPromptActivityResponsesIndicator';
import ActivityResultsView from 'components/ActivityResultsView/ActivityResultsView';
import { HelpOutline } from '@mui/icons-material';

const ActivitiesListItemBlock = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    marginBottom: -1,
    '&:first-of-type': {
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4
    },
    '&:last-of-type': {
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4
    }
}));

const ActivitiesListItem = ({ activity, focusGroupId }) => {
    const { t } = useTranslation();
    const activityType = ActivityType[activity.activityType];
    const ActivityIcon = activityType?.icon  ?? HelpOutline;
    const hasRun = activity.didRunSuccessfully;
    const [reportOpen, setReportOpen] = useState(false);
    const exploreReultsSupportedActivityTypes = [
        ActivityType.TextPrompt,
        ActivityType.Poll,
        ActivityType.Heatmap,
        ActivityType.Ranking
    ];
    const activityTypeAllowsExploreResults = exploreReultsSupportedActivityTypes.includes(activityType);

    return (
        <React.Fragment>
            <ActivitiesListItemBlock>
                <Stack direction='row' gap={2} alignItems='center'>
                    <ActivityIcon />
                    <Stack direction='column' sx={{flexGrow: 1}}>
                        <Typography variant='subtitle1'>
                            {activity.title}
                            { !hasRun && <Chip label={t('focusGroups.activities.didNotRun')} size='small' variant='outlined' sx={{marginLeft: 1}} /> }
                        </Typography>
                        <Typography variant='subtitle2'>
                            {t(`focusGroups.activityTypes.${activity.activityType}`)}
                        </Typography>
                    </Stack>

                    { activityType === ActivityType.TextPrompt && hasRun &&
                        <TextPromptActivityResponsesIndicator activityId={activity.id} />
                    }

                    { activityType === ActivityType.Poll && hasRun &&
                        <>
                            <PollActivityVotesIndicator activityId={activity.id} />
                            <PollActivityEngagementIndicator activityId={activity.id} focusGroupId={focusGroupId} />
                        </>
                    }

                    { activityType === ActivityType.Heatmap &&
                        <ActivitiesListItemSentimentOverview activityId={activity.id} />
                    }

                    {activityTypeAllowsExploreResults &&
                        <Button
                            variant="vl-secondary"
                            sx={{ flexShrink: 0 }}
                            onClick={() => setReportOpen(true)}
                            disabled={!hasRun}
                        >
                            {t('focusGroups.activities.exploreResults')}
                        </Button>
                    }
                </Stack>
            </ActivitiesListItemBlock>

            { reportOpen &&
                <ActivityResultsView
                    activityId={activity.id}
                    activityType={activityType}
                    focusGroupId={focusGroupId}
                    isOpen={reportOpen}
                    onClose={() => setReportOpen(false)}
                />
            }
        </React.Fragment>
    );
};

ActivitiesListItem.propTypes = {
    activity: PropTypes.object,
    focusGroupId: PropTypes.string
};

export default ActivitiesListItem;
